import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'

// ==================== Test Tube Volume ====================

export interface TestTubeVolumeGetResponse {
    volume: number
}

export interface TestTubeVolumeSetResponse {
    success: number
    errorMsg?: string
}

export async function getTestTubeVolume(clientId: string): Promise<TestTubeVolumeGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/test-tube-volume/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<TestTubeVolumeGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setTestTubeVolume(clientId: string, volume: number): Promise<TestTubeVolumeSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/test-tube-volume/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, volume })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<TestTubeVolumeSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

// ==================== Rinse Duration ====================

export interface RinseDurationGetResponse {
    duration: number
}

export interface RinseDurationSetResponse {
    success: number
    errorMsg?: string
}

export async function getRinseDuration(clientId: string): Promise<RinseDurationGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/rinse-duration/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<RinseDurationGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setRinseDuration(clientId: string, duration: number): Promise<RinseDurationSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/rinse-duration/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, duration })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<RinseDurationSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

// ==================== Cup Volume ====================

export interface CupVolumeGetResponse {
    volume: number
}

export interface CupVolumeSetResponse {
    success: number
    errorMsg?: string
}

export async function getCupVolume(clientId: string): Promise<CupVolumeGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/cup-volume/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<CupVolumeGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setCupVolume(clientId: string, volume: number): Promise<CupVolumeSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/cup-volume/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, volume })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<CupVolumeSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

// ==================== Bottle Volume ====================

export interface BottleVolumeGetResponse {
    volume: number
}

export interface BottleVolumeSetResponse {
    success: number
    errorMsg?: string
}

export async function getBottleVolume(clientId: string): Promise<BottleVolumeGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/bottle-volume/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<BottleVolumeGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setBottleVolume(clientId: string, volume: number): Promise<BottleVolumeSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/bottle-volume/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, volume })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<BottleVolumeSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

// ==================== Volume Measurement ====================

export interface CupVolumeMeasureStartResponse {
    success: number
    errorMsg?: string
}

export interface BottleVolumeMeasureStartResponse {
    success: number
    errorMsg?: string
}

export interface VolumeMeasureStopResponse {
    measuredVolume: number
}

export async function startCupVolumeMeasure(clientId: string, drinkId: number): Promise<CupVolumeMeasureStartResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/cup-volume-measure/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, drinkId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<CupVolumeMeasureStartResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function startBottleVolumeMeasure(clientId: string, drinkId: number): Promise<BottleVolumeMeasureStartResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/bottle-volume-measure/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, drinkId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<BottleVolumeMeasureStartResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function stopVolumeMeasure(clientId: string): Promise<VolumeMeasureStopResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/volume-measure/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<VolumeMeasureStopResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

// ==================== Extended General Settings ====================

export interface LedCountGetResponse {
    ledCount: number
}

export interface LedCountSetResponse {
    success: number
    errorMsg?: string
}

export async function getLedCount(clientId: string): Promise<LedCountGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/led-count/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LedCountGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setLedCount(clientId: string, ledCount: number): Promise<LedCountSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/led-count/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, ledCount })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LedCountSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface ManualPourModeGetResponse {
    holdEnabled: number
}

export interface ManualPourModeSetResponse {
    success: number
    errorMsg?: string
}

export async function getManualPourMode(clientId: string): Promise<ManualPourModeGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/manual-pour-mode/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<ManualPourModeGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setManualPourMode(clientId: string, holdEnabled: number): Promise<ManualPourModeSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/manual-pour-mode/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, holdEnabled })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<ManualPourModeSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface HomeLogoGetResponse {
    logoKey: string
}

export interface HomeLogoSetResponse {
    success: number
    errorMsg?: string
}

export async function getHomeLogo(clientId: string): Promise<HomeLogoGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/home-logo/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<HomeLogoGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setHomeLogo(clientId: string, logoKey: string): Promise<HomeLogoSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/home-logo/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, logoKey })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<HomeLogoSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface CalibrationStopConfirmGetResponse {
    confirmMs: number
}

export interface CalibrationStopConfirmSetResponse {
    success: number
    errorMsg?: string
}

export async function getCalibrationStopConfirm(clientId: string): Promise<CalibrationStopConfirmGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/calibration-stop-confirm/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<CalibrationStopConfirmGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setCalibrationStopConfirm(clientId: string, confirmMs: number): Promise<CalibrationStopConfirmSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/calibration-stop-confirm/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, confirmMs })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<CalibrationStopConfirmSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface PouringCompletionWindowGetResponse {
    completionWindowMs: number
}

export interface PouringCompletionWindowSetResponse {
    success: number
    errorMsg?: string
}

export async function getPouringCompletionWindow(clientId: string): Promise<PouringCompletionWindowGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/pouring-completion-window/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<PouringCompletionWindowGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setPouringCompletionWindow(clientId: string, completionWindowMs: number): Promise<PouringCompletionWindowSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/pouring-completion-window/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, completionWindowMs })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<PouringCompletionWindowSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface AutoRinseConfig {
    enabled: number
    hoursMask: number
    idleThresholdSec: number
    scheduleWaitSec: number
    durationSec: number
}

export interface AutoRinseConfigSetResponse {
    success: number
    errorMsg?: string
}

export interface AutoRinseStatus {
    running: number
    remainingSeconds: number
    canExtend: number
    hasLastRinse: number
    lastRinseElapsedSec: number
}

export interface AutoRinseActionResponse {
    success: number
    errorMsg?: string
}

export async function getAutoRinseConfig(clientId: string): Promise<AutoRinseConfig> {
    const response: Response = await fetch(`${HOST_URL}general-settings/auto-rinse/config/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<AutoRinseConfig> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setAutoRinseConfig(clientId: string, config: AutoRinseConfig): Promise<AutoRinseConfigSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/auto-rinse/config/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, ...config })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<AutoRinseConfigSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function getAutoRinseStatus(clientId: string): Promise<AutoRinseStatus> {
    const response: Response = await fetch(`${HOST_URL}general-settings/auto-rinse/status/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<AutoRinseStatus> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function startAutoRinse(clientId: string): Promise<AutoRinseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/auto-rinse/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<AutoRinseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function extendAutoRinse(clientId: string, extraSeconds: number): Promise<AutoRinseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/auto-rinse/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, extraSeconds })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<AutoRinseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function cancelAutoRinse(clientId: string): Promise<AutoRinseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/auto-rinse/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<AutoRinseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface HomeScreensaverResponse {
    enabled: number
    timeoutMin: number
}

export interface HomeScreensaverSetResponse {
    success: number
    errorMsg?: string
}

export async function getHomeScreensaver(clientId: string): Promise<HomeScreensaverResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/home-screensaver/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<HomeScreensaverResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setHomeScreensaver(clientId: string, enabled: number, timeoutMin: number): Promise<HomeScreensaverSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/home-screensaver/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enabled, timeoutMin })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<HomeScreensaverSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface RemoteLiveLogStreamGetResponse {
    enabled: number
}

export interface RemoteLiveLogStreamSetResponse {
    success: number
    errorMsg?: string
}

export async function getRemoteLiveLogStream(clientId: string): Promise<RemoteLiveLogStreamGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/remote-live-log-stream/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<RemoteLiveLogStreamGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setRemoteLiveLogStream(clientId: string, enabled: number): Promise<RemoteLiveLogStreamSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/remote-live-log-stream/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enabled })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<RemoteLiveLogStreamSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface RemoteTransportModeGetResponse {
    mode: number
}

export interface RemoteTransportModeSetResponse {
    success: number
    errorMsg?: string
}

export async function getRemoteTransportMode(clientId: string): Promise<RemoteTransportModeGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/remote-transport-mode/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<RemoteTransportModeGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setRemoteTransportMode(clientId: string, mode: number): Promise<RemoteTransportModeSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/remote-transport-mode/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, mode })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<RemoteTransportModeSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface SetupCompletedGetResponse {
    completed: number
}

export interface SetupCompletedSetResponse {
    success: number
    errorMsg?: string
}

export async function getSetupCompleted(clientId: string): Promise<SetupCompletedGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/setup-completed/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SetupCompletedGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setSetupCompleted(clientId: string, completed: number): Promise<SetupCompletedSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/setup-completed/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, completed })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SetupCompletedSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface DailyAutoRestartGetResponse {
    enabled: number
}

export interface DailyAutoRestartSetResponse {
    success: number
    errorMsg?: string
}

export async function getDailyAutoRestart(clientId: string): Promise<DailyAutoRestartGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/daily-auto-restart/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<DailyAutoRestartGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setDailyAutoRestart(clientId: string, enabled: number): Promise<DailyAutoRestartSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/daily-auto-restart/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enabled })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<DailyAutoRestartSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface HttpPollingIntervalGetResponse {
    intervalMs: number
}

export interface HttpPollingIntervalSetResponse {
    success: number
    errorMsg?: string
}

export async function getHttpPollingInterval(clientId: string): Promise<HttpPollingIntervalGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/http-polling-interval/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<HttpPollingIntervalGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setHttpPollingInterval(clientId: string, intervalMs: number): Promise<HttpPollingIntervalSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/http-polling-interval/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, intervalMs })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<HttpPollingIntervalSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface LoggingEnabledGetResponse {
    enabled: number
}

export interface LoggingEnabledSetResponse {
    success: number
    errorMsg?: string
}

export async function getLoggingEnabled(clientId: string): Promise<LoggingEnabledGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/logging-enabled/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LoggingEnabledGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setLoggingEnabled(clientId: string, enabled: number): Promise<LoggingEnabledSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/logging-enabled/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enabled })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LoggingEnabledSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface LogJournalEntry {
    seq: number
    tsEpochMs: number
    level: number
    tag: string
    message: string
}

export interface LogJournalResponse {
    success: number
    errorMsg?: string
    pageIndex: number
    pageSize: number
    totalEntries: number
    returnedCount: number
    entries: LogJournalEntry[]
}

export interface LogJournalClearResponse {
    success: number
    errorMsg?: string
}

export async function getLogJournal(clientId: string, pageIndex: number, pageSize: number): Promise<LogJournalResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/log-journal/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, pageIndex, pageSize })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LogJournalResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function clearLogJournal(clientId: string): Promise<LogJournalClearResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/log-journal/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LogJournalClearResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface LiveLogsPersistGetResponse {
    enabled: number
}

export interface LiveLogsPersistSetResponse {
    success: number
}

export interface PersistedLiveLogEntry {
    timestampMs: number
    line: string
}

export interface PersistedLiveLogsGetResponse {
    entries: PersistedLiveLogEntry[]
}

export interface PersistedLiveLogsClearResponse {
    success: number
}

export async function getLiveLogsPersist(clientId: string): Promise<LiveLogsPersistGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/live-logs/persist/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LiveLogsPersistGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setLiveLogsPersist(clientId: string, enabled: number): Promise<LiveLogsPersistSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/live-logs/persist/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enabled })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LiveLogsPersistSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.message)
    }

    return json.data
}

export async function getPersistedLiveLogs(clientId: string): Promise<PersistedLiveLogsGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/live-logs/persisted/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<PersistedLiveLogsGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function clearPersistedLiveLogs(clientId: string): Promise<PersistedLiveLogsClearResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/live-logs/persisted/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<PersistedLiveLogsClearResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.message)
    }

    return json.data
}

export interface InactivityTimeoutGetResponse {
    inactivityTimeoutSec: number
}

export interface InactivityTimeoutSetResponse {
    success: number
    errorMsg?: string
}

export async function getInactivityTimeout(clientId: string): Promise<InactivityTimeoutGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/inactivity-timeout/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<InactivityTimeoutGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setInactivityTimeout(clientId: string, inactivityTimeoutSec: number): Promise<InactivityTimeoutSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/inactivity-timeout/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, inactivityTimeoutSec })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<InactivityTimeoutSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface PublicTextEntry {
    keyId: number
    enOverride: string
    heOverride: string
}

export interface PublicTextsGetResponse {
    count: number
    entries: PublicTextEntry[]
}

export interface PublicTextSetResponse {
    success: number
    errorMsg?: string
}

export async function getPublicTexts(clientId: string): Promise<PublicTextsGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/public-texts/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<PublicTextsGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setPublicText(clientId: string, keyId: number, language: number, value: string): Promise<PublicTextSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/public-texts/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, keyId, language, value })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<PublicTextSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export interface LicenseStatusResponse {
    required: number
    statusCode: number
    canStartNewPour: number
    ntpSyncedOnce: number
    tempUnlockUsedForCycle: number
    trialDays: number
    defaultTempUnlockSec: number
    secondsUntilExpiry: number
    secondsUntilTempUnlockEnd: number
    activationEpochSec: number
    expiresEpochSec: number
    tempUnlockUntilEpochSec: number
    lastCheckEpochSec: number
    cycleId: number
    plan: string
    message: string
}

export interface LicenseActionResponse {
    success: number
    message: string
}

export interface LicensePolicyResponse {
    required: number
    trialDays: number
    defaultTempUnlockSec: number
}

export async function getLicenseStatus(clientId: string): Promise<LicenseStatusResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/license/status/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LicenseStatusResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function activateLicense(clientId: string, licenseKey: string): Promise<LicenseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/license/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, licenseKey })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LicenseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || !json.data || json.data.success === 0) {
        throw new Error(json.data?.message || json.message)
    }

    return json.data
}

export async function checkLicenseNow(clientId: string): Promise<LicenseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/license/check-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LicenseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || !json.data || json.data.success === 0) {
        throw new Error(json.data?.message || json.message)
    }

    return json.data
}

export async function tempUnlockLicense(clientId: string, requestedSeconds: number): Promise<LicenseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/license/temp-unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, requestedSeconds })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LicenseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || !json.data || json.data.success === 0) {
        throw new Error(json.data?.message || json.message)
    }

    return json.data
}

export async function getLicensePolicy(clientId: string): Promise<LicensePolicyResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/license/policy/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LicensePolicyResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setLicensePolicy(
    clientId: string,
    required: number,
    trialDays: number,
    defaultTempUnlockSec: number,
): Promise<LicenseActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/license/policy/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, required, trialDays, defaultTempUnlockSec })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<LicenseActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || !json.data || json.data.success === 0) {
        throw new Error(json.data?.message || json.message)
    }

    return json.data
}

export interface SanitationStatusResponse {
    statusCode: number
    enabled: number
    canStartNewPour: number
    ntpSyncedOnce: number
    intervalDays: number
    warningDays: number
    secondsUntilDue: number
    lastCompletedEpochSec: number
    nextDueEpochSec: number
    lastCheckEpochSec: number
    message: string
}

export interface SanitationActionResponse {
    success: number
    message: string
}

export interface SanitationPolicyResponse {
    enabled: number
    intervalDays: number
}

export async function getSanitationStatus(clientId: string): Promise<SanitationStatusResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/sanitation/status/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SanitationStatusResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function completeSanitation(clientId: string): Promise<SanitationActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/sanitation/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SanitationActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.message || json.message)
    }

    return json.data
}

export async function checkSanitationNow(clientId: string): Promise<SanitationActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/sanitation/check-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SanitationActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.message || json.message)
    }

    return json.data
}

export async function getSanitationPolicy(clientId: string): Promise<SanitationPolicyResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/sanitation/policy/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SanitationPolicyResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setSanitationPolicy(clientId: string, enabled: number, intervalDays: number): Promise<SanitationActionResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/sanitation/policy/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enabled, intervalDays })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SanitationActionResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.message || json.message)
    }

    return json.data
}

// ==================== Operation Mode ====================

export interface OperationModeGetResponse {
    mode: string
}

export interface OperationModeSetResponse {
    success: number
    errorMsg?: string
}

export async function getOperationMode(clientId: string): Promise<OperationModeGetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/operation-mode/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<OperationModeGetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setOperationMode(clientId: string, mode: string): Promise<OperationModeSetResponse> {
    const response: Response = await fetch(`${HOST_URL}general-settings/operation-mode/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, mode })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<OperationModeSetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}
